/**
 * Format a number as currency (CHF)
 * @param {number} amount - The amount to format
 * @param {string} [currency="CHF"] - The currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'CHF') => {
  if (amount === null || amount === undefined) return '0.00';

  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a date string
 * @param {string|Date} date - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat('en-CH', defaultOptions).format(new Date(date));
};

/**
 * Format a date as relative time (e.g. "2 days ago")
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted relative time
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const dateObj = new Date(date);
  const seconds = Math.floor((now - dateObj) / 1000);

  // Time intervals in seconds
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  if (seconds < 60) {
    return 'just now';
  }

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
  }

  return dateObj.toLocaleDateString();
};

/**
 * Format a number with specified decimal places
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {string} locale - The locale to use for formatting (default: 'en-CH')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2, locale = 'en-CH') => {
  if (value === undefined || value === null) return '';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};
